import React, { useState, useEffect, useRef } from 'react';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { buildThumbnailUrl, thumbnailCacheKey } from '../utils/thumbnailUrl';
import { fmtBytes } from '../utils/format';
import { fmtDate } from '../utils/fileFacts';
import { columnLabel } from '../config/fileColumns';

const getNestedVal = (obj, path) => path.split('.').reduce((acc, part) => acc && acc[part], obj);

// Every column used to render as `String(value)`. That is fine for name and
// mimetype, but SIZE (on by default) showed a raw byte count and a Firestore
// timestamp stringified to "[object Object]" — while the grid view formatted
// the very same fields correctly. Format per column instead.
const COLUMN_FORMATTERS = {
    'metadata.size': (v) => fmtBytes(Number(v)),
    'metadata.timeOfCreation': (v) => fmtDate(v),
};

const renderCell = (file, col) => {
    const raw = getNestedVal(file, col);
    if (raw == null || raw === '') return '-';
    const format = COLUMN_FORMATTERS[col];
    const out = format ? format(raw) : (Array.isArray(raw) ? raw.join(', ') : String(raw));
    return out || '-';
};

// Per-row label. A shared "Select file" on every checkbox gives screen-reader
// users a list of identical controls with no way to tell which row they're on.
const rowLabel = (file) => `Select ${getNestedVal(file, 'metadata.name') || 'file'}`;

/**
 * Master checkbox for the table header. Shows the indeterminate (dash) state
 * when only part of the result set is selected — that has to be set as a DOM
 * property, there is no HTML attribute for it.
 */
const SelectAllCheckbox = ({ allSelected, someSelected, onToggle }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = !allSelected && someSelected;
    }, [allSelected, someSelected]);

    return (
        <input
            ref={ref}
            type="checkbox"
            checked={allSelected}
            onChange={onToggle}
            aria-label={allSelected ? 'Clear selection' : 'Select all results'}
            title={allSelected ? 'Clear selection' : 'Select all results'}
        />
    );
};

// L-14: only virtualize for large workspaces. Below this threshold the common
// case renders the exact existing <table> markup (risk-free).
const VIRTUALIZE_THRESHOLD = 150;

// Matches .file-list-table td padding (10px 15px) so windowed rows line up.
const ROW_HEIGHT = 51;

// Fixed-width leading columns mirror the existing <th> widths.
const CHECKBOX_WIDTH = 40;
const THUMB_WIDTH = 60;
const CELL_PADDING = '0 15px';

const ListThumb = ({ file }) => {
    const [url, setUrl] = useState(null);
    // Re-resolve when the thumbnail itself changes, not just when the row swaps
    // to a different file — a recaptured thumbnail is a new URL.
    const version = thumbnailCacheKey(file);
    useEffect(() => {
        buildThumbnailUrl(file).then(setUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file.id, version]);
    return url ? <img src={url} alt="" className="table-thumb" loading="lazy" decoding="async" /> : null;
};

const FileListView = ({
    files,
    selection,
    visibleColumns,
    onFileClick,
    onCheckboxClick,
    allSelected = false,
    onToggleSelectAll,
}) => {
    const someSelected = selection.length > 0;
    const headerCheckbox = (
        <SelectAllCheckbox
            allSelected={allSelected}
            someSelected={someSelected}
            onToggle={onToggleSelectAll}
        />
    );

    // Common case: keep the exact existing <table> markup unchanged.
    if (files.length <= VIRTUALIZE_THRESHOLD) {
        return (
            <div className="file-list-view-container">
                <table className="file-list-table">
                    <thead>
                    <tr>
                        <th scope="col" style={{ width: '40px' }}>{headerCheckbox}</th>
                        <th scope="col" style={{ width: '60px' }}></th>
                        {visibleColumns.map(col => (
                            <th scope="col" key={col}>{columnLabel(col)}</th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {files.map(file => {
                        const isSelected = selection.includes(file.id);
                        return (
                            <tr
                                key={file.id}
                                className={isSelected ? 'selected' : ''}
                                onClick={(e) => onFileClick(file.id, e)} // Pass Event
                                // L-13: clickable rows are focusable and respond to Enter.
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter') onFileClick(file.id, e); }}
                                aria-selected={isSelected}
                            >
                                <td onClick={e => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => onCheckboxClick(file.id)}
                                        aria-label={rowLabel(file)}
                                    />
                                </td>
                                <td>
                                    <ListThumb file={file} />
                                </td>
                                {visibleColumns.map(col => (
                                    <td key={col}>
                                        <div className="cell-content">
                                            {renderCell(file, col)}
                                        </div>
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        );
    }

    // Large workspace: keep the <thead> header fixed at top and window the body
    // rows with div-based cells styled to align with the header columns.
    const renderHeader = () => (
        <table className="file-list-table" style={{ tableLayout: 'fixed' }}>
            <thead>
            <tr>
                <th scope="col" style={{ width: '40px' }}>{headerCheckbox}</th>
                <th scope="col" style={{ width: '60px' }}></th>
                {visibleColumns.map(col => (
                    <th scope="col" key={col}>{columnLabel(col)}</th>
                ))}
            </tr>
            </thead>
        </table>
    );

    const Row = ({ index, style }) => {
        const file = files[index];
        if (!file) return null;
        const isSelected = selection.includes(file.id);
        return (
            <div
                // Selection background/ring live in CSS (.file-list-row.selected).
                // They used to be inline, and an inline backgroundColor outranks
                // any class — which silently killed :hover on every windowed row,
                // so large workspaces lost the row highlight the small ones had.
                style={{
                    ...style,
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid var(--qd-border)',
                    cursor: 'pointer',
                    fontSize: 'var(--qd-text-base)',
                }}
                className={`file-list-row${isSelected ? ' selected' : ''}`}
                onClick={(e) => onFileClick(file.id, e)} // Pass Event
                // L-13: clickable rows are focusable and respond to Enter.
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') onFileClick(file.id, e); }}
                aria-selected={isSelected}
                role="row"
            >
                <div style={{ width: CHECKBOX_WIDTH, padding: CELL_PADDING, boxSizing: 'border-box', flex: '0 0 auto' }} onClick={e => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onCheckboxClick(file.id)}
                        aria-label={rowLabel(file)}
                    />
                </div>
                <div style={{ width: THUMB_WIDTH, padding: CELL_PADDING, boxSizing: 'border-box', flex: '0 0 auto' }}>
                    <ListThumb file={file} />
                </div>
                {visibleColumns.map(col => (
                    <div key={col} style={{ flex: 1, padding: CELL_PADDING, boxSizing: 'border-box', minWidth: 0 }}>
                        <div className="cell-content">
                            {renderCell(file, col)}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="file-list-view-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {renderHeader()}
            <div style={{ flex: 1, minHeight: 0 }}>
                <AutoSizer>
                    {({ height, width }) => {
                        // Unconstrained parent: fall back to a plain table so the
                        // view never blanks.
                        if (height < 1 || width < 1) {
                            return (
                                <table className="file-list-table">
                                    <tbody>
                                    {files.map(file => {
                                        const isSelected = selection.includes(file.id);
                                        return (
                                            <tr
                                                key={file.id}
                                                className={isSelected ? 'selected' : ''}
                                                onClick={(e) => onFileClick(file.id, e)}
                                                tabIndex={0}
                                                onKeyDown={(e) => { if (e.key === 'Enter') onFileClick(file.id, e); }}
                                                aria-selected={isSelected}
                                            >
                                                <td onClick={e => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => onCheckboxClick(file.id)}
                                                        aria-label={rowLabel(file)}
                                                    />
                                                </td>
                                                <td>
                                                    <ListThumb file={file} />
                                                </td>
                                                {visibleColumns.map(col => (
                                                    <td key={col}>
                                                        <div className="cell-content">
                                                            {renderCell(file, col)}
                                                        </div>
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            );
                        }

                        return (
                            <FixedSizeList
                                height={height}
                                width={width}
                                itemCount={files.length}
                                itemSize={ROW_HEIGHT}
                            >
                                {Row}
                            </FixedSizeList>
                        );
                    }}
                </AutoSizer>
            </div>
        </div>
    );
};

export default FileListView;
